/**
 * advisorInviteCtaSuppression — permanent per-placement dismiss for the
 * WL-GTM-7 "Kanzlei einladen"-CTA (modules-loop-b.md WL-GTM-7, Reviewer-Gate
 * "kein Dark-Pattern" — dismissbar, kein Nag-Loop).
 *
 * Unlike {@link ../utils/paywallTrigger4Suppression} (a 7-day COOLDOWN for a
 * premium upsell), this is a PERMANENT dismiss: WL-GTM-7 is a low-stakes,
 * purely informational invite-nudge, not a paywall. Once a user explicitly
 * closes it via the X-dismiss, it must never reappear in the SAME placement
 * on this device/browser — re-nagging a user who already said "not now"
 * about inviting their Kanzlei would itself be a nag-loop.
 *
 * Each of the three WL-GTM-7 placements (dashboard card, DATEV-export-moment
 * card, import-instructions footer) has its OWN key so dismissing one does
 * not hide the others (mirrors `paywallTrigger4Suppression`'s per-variant
 * cooldown split).
 *
 * Best-effort: `localStorage` unavailability (private browsing, quota) never
 * blocks the CTA from showing — fail-open on storage errors, since this is a
 * UX nicety, not a security/legal control (same rationale as
 * `paywallTrigger4Suppression`).
 *
 * @module utils/advisorInviteCtaSuppression
 */

/** The three WL-GTM-7 CTA surfaces (modules-loop-b.md WL-GTM-7 "(a)/(b)/(c)"). */
export type AdvisorInviteCtaPlacement = 'dashboard_card' | 'export_moment_card' | 'import_footer';

function storageKey(placement: AdvisorInviteCtaPlacement): string {
  return `faebill_advisor_invite_cta_${placement}_dismissed`;
}

/**
 * `true` if the user has already permanently dismissed this placement.
 * Fail-open: any storage read error resolves to `false` (never silently
 * hide the CTA due to an unrelated storage failure).
 *
 * @param placement - which WL-GTM-7 surface is asking.
 */
export function isAdvisorInviteCtaDismissed(placement: AdvisorInviteCtaPlacement): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(storageKey(placement)) === '1';
  } catch {
    return false;
  }
}

/**
 * Marks `placement` as permanently dismissed. Best-effort: a storage write
 * failure is silently ignored (fail-open — the CTA may reappear on the next
 * render instead of blocking anything).
 *
 * @param placement - which WL-GTM-7 surface was dismissed.
 */
export function dismissAdvisorInviteCta(placement: AdvisorInviteCtaPlacement): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(storageKey(placement), '1');
  } catch {
    // best-effort — ignore.
  }
}
